/**
 * @author       hugo@zumper.com (Hugo Pineda)
 * @copyright    Copyright (c) 2020, Zumper
 */

import { deleteUndefineds } from '@zcode-ui/map'
import { selectIsOnlyActiveUnitOfMultiUnit, selectIsSingleUnit } from '@zcode-ui/perm-address'
import { isDetailPageNew, selectIsMobile } from '@zcode-ui/web-zumper-global-state'
import type { TopListable } from '@zcode/bizos'
import { LeaseType, getEnumLabel } from '@zcode/enums'
import { isErrorish } from '@zcode/error-filters'
import { DETAIL_PAGE_TYPES, EVENT_NAMES, PAGE_NAMES, PROP_NAMES } from '@zcode/mixpanel'
import type { Origin } from '@zcode/nr-utils'
import { toTitleCase } from '@zcode/nr/es/shared/utils'
import type { TitleCase } from '@zcode/nr/es/shared/utils/toTitleCase'
import type {
  Listable,
  MaybeEmptyProperty,
  Pin,
  RawPinInventorySearch,
  State,
} from '@zcode/state-type'
import type { Empty, MaybeEmpty } from '@zcode/utility-types'
import invariant from 'tiny-invariant'
import { getCurrentCityPath } from '../../../inspectors/index'
import { logger } from '../../../logger'
import { selectEntity } from '../../detail/modules/entity/selectors/index'
import { getDetailProps } from './getDetailProps'
import { getFilterProps } from './getFilterProps'
import { getFormattedOrigin } from './getFormattedOrigin'
import { getLeaseLengthType } from './getLeaseLengthType'
import { getPageNameFromRouteProps } from './getPageNameFromRouteProps'
import { mapFeatures } from './mapFeatures'
import type { PageName } from './types'

export type MixpanelOrigin =
  | Origin
  | 'Tour Booking'
  | 'Detail Image'
  | 'Detail Video'
  | 'Detail Tour'
  | 'booknow'
  | 'pin'

const EVENTS_WITH_DETAIL_PROPS = [
  EVENT_NAMES.VIEW_PAGE,
  EVENT_NAMES.DETAIL_PAGE_TOP_SIMILAR_LISTINGS,
  EVENT_NAMES.VIEW_SECTION,
  EVENT_NAMES.CLICK,
  EVENT_NAMES.SEND_MESSAGE,
  EVENT_NAMES.CALL,
  EVENT_NAMES.REQUEST_TOUR,
  EVENT_NAMES.CLICK_TOUR_AVAILABILITY,
  EVENT_NAMES.CLICK_MESSAGE,
] as const

const _getCommonProps = ({
  state,
  entity,
  pageName,
  origin,
  event,
}: {
  state: State
  entity?:
    | Listable
    | Pin<RawPinInventorySearch>
    | MaybeEmptyProperty
    | TopListable
    | null
    | undefined
  pageName?: PageName | (string & {})
  origin?: MixpanelOrigin
  event?: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES]
}) => {
  let pageType: DETAIL_PAGE_TYPES | undefined
  let buildingId: number | undefined
  let cityState: NonNullable<typeof entity>['cityState']
  let features: ReturnType<typeof mapFeatures> | undefined
  let feedName: NonNullable<typeof entity>['feedName'] | undefined
  let leaseType: TitleCase<keyof typeof LeaseType> | undefined
  let listingId: number | null | undefined
  let maxLeaseDays: NonNullable<typeof entity>['maxLeaseDays']
  let minLeaseDays: NonNullable<typeof entity>['minLeaseDays']
  let promotionType: NonNullable<typeof entity>['promotionType']
  let specials: NonNullable<typeof entity>['specials']

  const formattedOrigin = getFormattedOrigin(origin)

  // entity can be passed in from message modals, if not we look in redux
  if (!entity) {
    entity = selectEntity(state)
  }

  // Sometimes we render the "detail" page on /address/... URLs, so if a component told us
  // we're on the detail page, let's double-check and see if it should the address page instead
  if (pageName === PAGE_NAMES.DETAIL) {
    const isMobile = selectIsMobile(state)
    const verifiedPageName = getPageNameFromRouteProps({
      routeProps: {
        location: { pathname: window?.location?.pathname, search: window.location.search },
      },
      isMobile,
    })
    if (verifiedPageName === PAGE_NAMES.PERM_ADDRESS) {
      pageName = verifiedPageName
    }
  }

  // if no pageName is passed in, we can figure it out from current location
  if (!pageName) {
    const isMobile = selectIsMobile(state)

    pageName = getPageNameFromRouteProps({
      routeProps: {
        location: { pathname: window?.location?.pathname, search: window.location.search },
      },
      isMobile,
    })
  }

  try {
    if (entity) {
      cityState = entity.cityState
      leaseType = entity.leaseType
        ? toTitleCase(getEnumLabel(LeaseType, entity.leaseType))
        : undefined
      maxLeaseDays = entity.maxLeaseDays ?? undefined
      minLeaseDays = entity.minLeaseDays ?? undefined
      feedName = entity.feedName
      specials = entity.specials ?? undefined
      promotionType = entity.promotionType
      if (pageName !== PAGE_NAMES.SEARCH && pageName !== PAGE_NAMES.SEARCH_LIST) {
        const isNewDetail = isDetailPageNew(state)
        if (pageName === PAGE_NAMES.PERM_ADDRESS) {
          pageType = selectIsSingleUnit(state)
            ? DETAIL_PAGE_TYPES.PERM_ADDRESS_SINGLE_UNIT
            : selectIsOnlyActiveUnitOfMultiUnit(state)
              ? DETAIL_PAGE_TYPES.PERM_ADDRESS_SINGLE_ACTIVE_UNIT
              : DETAIL_PAGE_TYPES.PERM_ADDRESS
        } else if (entity.isBuilding) {
          invariant(isNewDetail, 'old detail page unexpectly used for building')
          pageType = DETAIL_PAGE_TYPES.BUILDING
        } else {
          pageType = isNewDetail ? DETAIL_PAGE_TYPES.LISTING : DETAIL_PAGE_TYPES.OLD_LISTING
        }
      }
      if (entity.features) {
        features = mapFeatures(entity.features)
      }
      if (entity.buildingId) {
        buildingId = entity.buildingId
      } else {
        listingId = entity.listingId
      }
    } else {
      cityState = getCurrentCityPath(state)
    }
  } catch (e) {
    logger.error(`Error gathering props for mixpanel: ${isErrorish(e) ? e.message : String(e)}`)
  }

  const detailPageProps =
    entity &&
    event &&
    EVENTS_WITH_DETAIL_PROPS.includes(event) &&
    (pageName === PAGE_NAMES.DETAIL || pageName === PAGE_NAMES.PERM_ADDRESS)
      ? getDetailProps(entity, state, pageName)
      : {}

  const leaseLenthType = getLeaseLengthType(state)

  const allProps = {
    // common props
    [PROP_NAMES.BUILDING_ID]: buildingId,
    [PROP_NAMES.CITY_STATE_LOCATION]: cityState,
    [PROP_NAMES.DETAIL_PAGE_TYPE]: pageType,
    [PROP_NAMES.FEED_NAME]: feedName,
    [PROP_NAMES.FORMATTED_ORIGIN]: formattedOrigin,
    [PROP_NAMES.LEASE_TYPE]: leaseType,
    [PROP_NAMES.LISTING_ID]: listingId,
    [PROP_NAMES.MAX_LEASE_DAYS]: maxLeaseDays,
    [PROP_NAMES.MIN_LEASE_DAYS]: minLeaseDays,
    [PROP_NAMES.ORIGIN]: origin,
    [PROP_NAMES.PAGE_NAME]: pageName,
    [PROP_NAMES.PROMO_TYPE]: promotionType,
    [PROP_NAMES.PROPERTY_FEATURE]: features,
    [PROP_NAMES.SPECIALS]: specials,
    [PROP_NAMES.LEASE_LENGTH_TYPE]: leaseLenthType,

    ...getFilterProps(state),

    ...detailPageProps,
  }

  return deleteUndefineds(allProps)
}

function hasState(
  props:
    | {
        state?: State
        entity?:
          | Listable
          | Pin<RawPinInventorySearch>
          | MaybeEmptyProperty
          | TopListable
          | null
          | undefined
        pageName?: PageName | (string & {})
        origin?: MixpanelOrigin
        event?: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES]
      }
    | undefined
): props is typeof props & { state: State } {
  return !!props?.state
}

export function getCommonProps(props: {
  state: State
  entity?:
    | Listable
    | Pin<RawPinInventorySearch>
    | MaybeEmptyProperty
    | TopListable
    | null
    | undefined
  pageName?: PageName | (string & {})
  origin?: MixpanelOrigin
  event?: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES]
}): ReturnType<typeof _getCommonProps>
export function getCommonProps(props?: {
  state?: undefined
  entity?:
    | Listable
    | Pin<RawPinInventorySearch>
    | MaybeEmptyProperty
    | TopListable
    | null
    | undefined
  pageName?: PageName | (string & {})
  origin?: MixpanelOrigin
  event?: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES]
}): Empty<ReturnType<typeof _getCommonProps>>
export function getCommonProps(props?: {
  state?: State
  entity?:
    | Listable
    | Pin<RawPinInventorySearch>
    | MaybeEmptyProperty
    | TopListable
    | null
    | undefined
  pageName?: PageName | (string & {})
  origin?: MixpanelOrigin
  event?: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES]
}): MaybeEmpty<ReturnType<typeof _getCommonProps>>
export function getCommonProps(props?: {
  state?: State
  entity?:
    | Listable
    | Pin<RawPinInventorySearch>
    | MaybeEmptyProperty
    | TopListable
    | null
    | undefined
  pageName?: PageName | (string & {})
  origin?: MixpanelOrigin
  event?: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES]
}) {
  if (hasState(props)) {
    return _getCommonProps(props)
  }
  return {}
}
